import type {
  AdditionOut,
  BillElementItemOut,
  BillElementOut,
} from "../../../../api/bills";

/** Добавка в ред за показване: представителят носи имена/цена, количеството и
 *  сумите са сборни за цялата слята група. */
export interface DisplayAddition {
  representative: AdditionOut;
  quantity: number;
  /** Сбор на записаните total_sum (крайната стойност след корекции). */
  totalSum: number;
  /** Сбор количество × цена (стойност без корекции). */
  baseSum: number;
}

/** Ред от детайлите на масата — един запис от сметката или няколко напълно
 *  еднакви, слети в един със сборно количество („Обобщено"). */
export interface DisplayRow {
  key: string;
  /** Представителят на групата — всички в нея са напълно еднакви. */
  item: BillElementItemOut;
  quantity: number;
  /** Сбор на total_sum на самите продукти (без добавките). */
  totalSum: number;
  additions: DisplayAddition[];
}

/**
 * Партидите на сметката в реда на показване в детайла: без празните (всичко в
 * тях е сторнирано — BE не връща сторнираните редове), хронологично. Общ за
 * OrderDetails и за стрелките по редовете (useOrderItemsKeyboard).
 */
export const sortBillElementsForDisplay = (
  elements: BillElementOut[],
): BillElementOut[] =>
  elements
    .filter((element) => element.items.length > 0)
    .sort(
      (a, b) =>
        new Date(a.order_time).getTime() - new Date(b.order_time).getTime(),
    );

export const toDisplayRow = (item: BillElementItemOut): DisplayRow => ({
  key: `item-${item.id}`,
  item,
  quantity: parseFloat(item.quantity),
  totalSum: parseFloat(item.total_sum),
  additions: item.additions.map((addition) => ({
    representative: addition,
    quantity: parseFloat(addition.quantity),
    totalSum: parseFloat(addition.total_sum),
    baseSum: parseFloat(addition.quantity) * parseFloat(addition.price),
  })),
});

/**
 * Ключ за обобщаване — огледало на orderRowKey от „Преглед на поръчката", но
 * върху записаните в сметката редове. Слива се САМО напълно еднакъв ред:
 * същият продукт (и същото обедно меню) със същата цена/процент/статус, същите модификатори и
 * коментари и същите добавки. Количеството на добавката влиза в ключа НА
 * ЕДИНИЦА продукт, за да се слее „2 × продукт с 2 × добавка" с
 * „1 × продукт с 1 × добавка".
 */
const displayRowKey = (item: BillElementItemOut): string => {
  const itemQuantity = parseFloat(item.quantity) || 1;
  return JSON.stringify([
    item.item_id,
    item.lunch_menu_id ?? null,
    item.price,
    item.percent,
    item.custom_price,
    item.status,
    item.served,
    item.modifiers.map((modifier) => modifier.modifier_id).sort(),
    item.additions
      .map((addition) =>
        JSON.stringify([
          addition.addition_item_id,
          addition.price,
          addition.percent,
          addition.custom_price,
          Math.round((parseFloat(addition.quantity) / itemQuantity) * 10000),
        ]),
      )
      .sort(),
  ]);
};

/**
 * Слива еднаквите редове от всички партиди в общ списък („Обобщено") —
 * партидните граници (час, коментар към поръчката) не участват в изгледа.
 */
export const buildSummarizedRows = (
  elements: BillElementOut[],
): DisplayRow[] => {
  const rowByKey = new Map<string, DisplayRow>();
  const rows: DisplayRow[] = [];

  elements.forEach((element) => {
    element.items.forEach((item) => {
      const key = displayRowKey(item);
      const existing = rowByKey.get(key);
      if (!existing) {
        const row = { ...toDisplayRow(item), key: `group-${key}` };
        rowByKey.set(key, row);
        rows.push(row);
        return;
      }

      existing.quantity += parseFloat(item.quantity);
      existing.totalSum += parseFloat(item.total_sum);
      item.additions.forEach((addition) => {
        // Ключът гарантира еднаква структура на добавките в групата
        const merged = existing.additions.find(
          (display) =>
            display.representative.addition_item_id ===
              addition.addition_item_id &&
            display.representative.price === addition.price &&
            display.representative.percent === addition.percent,
        );
        if (!merged) return;
        merged.quantity += parseFloat(addition.quantity);
        merged.totalSum += parseFloat(addition.total_sum);
        merged.baseSum +=
          parseFloat(addition.quantity) * parseFloat(addition.price);
      });
    });
  });

  return rows;
};
